import { isMatch } from 'lodash';
import { ServiceProvider } from '@lambda/commons/apis/elasticsearch/types';
import {
  EU_STORES,
  REEBELO_STORES_WITH_CARRIER,
  ReebeloStoreT,
} from '@lambda/reebelo/src/types';
/* eslint-disable no-param-reassign */

interface ServiceProviderVariants {
  cellularStatus: string;
  cellularNetwork?: string;
  networkProvider?: string;
}

const unlockedCondition: ServiceProviderVariants = {
  cellularStatus: 'Unlocked',
  cellularNetwork: 'CDMA',
};

const SERVICE_PROVIDER_CONDITION_MAPPING: {
  [serviceProvider in ServiceProvider]: Array<ServiceProviderVariants>;
} = {
  Unlocked: [unlockedCondition],
  'AT&T': [
    unlockedCondition,
    {
      cellularStatus: 'Unlocked',
      cellularNetwork: 'GSM',
    },
    {
      cellularStatus: 'Locked',
      networkProvider: 'AT&T',
    },
  ],
  'T-Mobile': [
    unlockedCondition,
    {
      cellularStatus: 'Unlocked',
      cellularNetwork: 'GSM',
    },
    {
      cellularStatus: 'Locked',
      networkProvider: 'T-Mobile',
    },
  ],
  Verizon: [
    unlockedCondition,
    {
      cellularStatus: 'Locked',
      networkProvider: 'Verizon',
    },
  ],
  Sprint: [
    unlockedCondition,
    {
      cellularStatus: 'Locked',
      networkProvider: 'Sprint',
    },
  ],
};

// Forward mapping: a carrier variant value -> its canonical cellular fields.
// Mirrors cobalt's `reconcileCarrierAttributes` so a server-side publish resolves
// the same cellular attributes the self-mapping form would submit for a carrier.
export const CARRIER_TO_CELLULAR_FIELDS: Record<
  string,
  Required<ServiceProviderVariants>
> = {
  'Fully Unlocked': {
    cellularStatus: 'Unlocked',
    cellularNetwork: 'CDMA',
    networkProvider: 'Verizon',
  },
  'GSM Unlocked': {
    cellularStatus: 'Unlocked',
    cellularNetwork: 'GSM',
    networkProvider: 'AT&T',
  },
  'AT&T': {
    cellularStatus: 'Locked',
    cellularNetwork: 'GSM',
    networkProvider: 'AT&T',
  },
  Verizon: {
    cellularStatus: 'Locked',
    cellularNetwork: 'CDMA',
    networkProvider: 'Verizon',
  },
  'T-Mobile': {
    cellularStatus: 'Locked',
    cellularNetwork: 'GSM',
    networkProvider: 'T-Mobile',
  },
};

export const carrierToCellularFields = (
  carrier: string | undefined,
): Required<ServiceProviderVariants> | undefined => {
  if (!carrier) {
    return undefined;
  }

  return CARRIER_TO_CELLULAR_FIELDS[carrier];
};

export const convertCellularFieldsToServiceProvider = (
  extractedTags: Partial<ServiceProviderVariants>,
): Array<ServiceProvider> =>
  Object.keys(SERVICE_PROVIDER_CONDITION_MAPPING)
    .map((serviceProvider) => {
      const conditions =
        SERVICE_PROVIDER_CONDITION_MAPPING[serviceProvider as ServiceProvider];

      if (conditions.some((condition) => isMatch(extractedTags, condition))) {
        return serviceProvider;
      }

      return undefined;
    })
    .filter(Boolean) as Array<ServiceProvider>;

const STORES_WITH_FIXED_CARRIER: ReebeloStoreT[] = [
  'reebelo-uk',
  'reebelo-ca',
  ...REEBELO_STORES_WITH_CARRIER,
  ...EU_STORES,
];

const FIXED_CARRIER = 'Fully Unlocked';

export const applyFixedCarrierForStore = (
  variants: Record<string, string>,
  store: ReebeloStoreT,
  category: string | undefined,
): void => {
  if (category !== 'Smartphones') {
    return;
  }

  if (
    REEBELO_STORES_WITH_CARRIER.includes(store) &&
    (!variants.carrier || variants.carrier !== FIXED_CARRIER)
  ) {
    throw new Error(
      `Cannot set carrier to "${variants.carrier}" for store ${store}: only ${FIXED_CARRIER} is allowed`,
    );
  }

  if (STORES_WITH_FIXED_CARRIER.includes(store)) {
    variants.carrier = FIXED_CARRIER;
  } else {
    delete variants.carrier;
  }
  delete variants.cellularNetwork;
  delete variants.cellularStatus;
  delete variants.networkProvider;
};
